import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Page, Card, BlockStack, IndexTable, Text, Badge, Button, TextField, Select, EmptyState } from '@shopify/polaris';
import { estimatesApi } from '@/services';
import type { Estimate, EstimateStatus } from '@/services/estimates';

const STATUS_TONE: Record<EstimateStatus, 'success' | 'attention' | 'info' | 'warning' | 'critical' | undefined> = {
  draft: undefined, sent: 'info', accepted: 'success', converted: 'success', expired: 'warning',
};

export default function EstimatesList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [type, setType] = useState<string>('');
  const [status, setStatus] = useState<string>('');

  const query = useQuery({
    queryKey: ['estimates.list', { search, type, status }],
    queryFn: () => estimatesApi.list({
      search: search || undefined,
      type: (type || undefined) as any,
      status: status || undefined,
    }),
  });

  const rows = (query.data?.data ?? []).map((e: Estimate, i) => (
    <IndexTable.Row id={e.id} key={e.id} position={i}>
      <IndexTable.Cell>
        <Button variant="plain" onClick={() => navigate(`/estimates/${e.id}`)}>{e.estimateNumber}</Button>
      </IndexTable.Cell>
      <IndexTable.Cell>{e.type === 'sale' ? 'Sale' : 'Rental'}</IndexTable.Cell>
      <IndexTable.Cell>{`${e.patientFirstName} ${e.patientLastName}`}</IndexTable.Cell>
      <IndexTable.Cell>{`$${(e.totalCents / 100).toFixed(2)}`}</IndexTable.Cell>
      <IndexTable.Cell><Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge></IndexTable.Cell>
      <IndexTable.Cell>{new Date(e.estimateDate).toLocaleDateString()}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Estimates"
      primaryAction={{ content: 'New Estimate', onAction: () => navigate('/estimates/new') }}
    >
      <Card>
        <BlockStack gap="300">
          <BlockStack gap="200">
            <TextField label="Search" value={search} onChange={setSearch} autoComplete="off" placeholder="Estimate # or patient name" />
            <Select label="Type" options={[{ label: 'All', value: '' }, { label: 'Sale', value: 'sale' }, { label: 'Rental', value: 'rental' }]} value={type} onChange={setType} />
            <Select label="Status" options={[{ label: 'All', value: '' }, { label: 'Draft', value: 'draft' }, { label: 'Sent', value: 'sent' }, { label: 'Accepted', value: 'accepted' }, { label: 'Converted', value: 'converted' }, { label: 'Expired', value: 'expired' }]} value={status} onChange={setStatus} />
          </BlockStack>

          {query.isLoading ? <Text as="p">Loading…</Text> :
           rows.length === 0 ? <EmptyState heading="No estimates yet" image="">Create your first estimate.</EmptyState> :
           <IndexTable
             resourceName={{ singular: 'estimate', plural: 'estimates' }}
             itemCount={rows.length}
             headings={[{ title: 'Number' }, { title: 'Type' }, { title: 'Patient' }, { title: 'Total' }, { title: 'Status' }, { title: 'Date' }]}
             selectable={false}
           >
             {rows}
           </IndexTable>}
        </BlockStack>
      </Card>
    </Page>
  );
}
